/**
 * useUserAnalytics Hook
 * Fetches comprehensive user analytics for admin panel
 */

import { apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import type { UserAnalytics } from '@parlay-picker/shared/userAnalytics';
import { useCallback, useEffect, useState } from 'react';

interface UseUserAnalyticsResult {
  analytics: UserAnalytics | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useUserAnalytics(): UseUserAnalyticsResult {
  const { authFetch } = useAuth();
  const [analytics, setAnalytics] = useState<UserAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(apiUrl('/api/admin/analytics/users'));

      if (!response.ok) {
        throw new Error('Failed to fetch user analytics');
      }

      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    analytics,
    loading,
    error,
    refetch: fetchAnalytics,
  };
}
